// Adapter for /api/curator/* and the pack lifecycle endpoints used by
// the Curator dashboard. The curator session lives in a dedicated
// `qbranch_curator` cookie set by POST /api/curator/login (no org
// connection required).

import { apiCall, apiSoft } from "./http";

export interface CuratorPack {
  id: string;
  name: string;
  description?: string;
  industry?: string;
  industry_tags?: string[];
  status: "draft" | "submitted" | "approved" | "featured" | "rejected" | "built_in";
  source?: string;
  use_count?: number;
  rating_avg?: number;
  rating_count?: number;
  created_at?: string;
  updated_at?: string;
  notes?: string;
  creator_email?: string;
  icon_key?: string | null;
  logo_url?: string | null;
  version?: number;
}

export interface CuratorAnalytics {
  total?: number;
  by_status?: Record<string, number>;
  pending_review?: number;
  top_by_usage?: { id: string; name: string; use_count: number }[];
  top_by_rating?: { id: string; name: string; rating_avg: number }[];
  jobs_by_status?: Record<string, number>;
}

export const curatorApi = {
  me: () => apiSoft<{ logged_in: boolean; email?: string; name?: string }>("/api/curator/me"),
  checkEmail: (email: string) =>
    // 2026-05-03: ``has_password`` was dropped from the response — the
    // public combination of {exists, has_password} let an unauthenticated
    // visitor enumerate which curator/admin emails had not yet set a
    // password and then claim them via /login's old auto-create-password
    // branch. The login flow now relies on a single 401 from /login
    // ("Account not initialized…") when the curator hasn't been
    // bootstrapped out-of-band, instead of forking the UI on this flag.
    apiSoft<{ exists: boolean }>(
      `/api/curator/check-email?email=${encodeURIComponent(email)}`,
    ),
  login: (email: string, password: string) =>
    apiCall<{ ok: boolean }>("/api/curator/login", {
      method: "POST",
      body: { email, password },
    }),
  logout: () => apiSoft("/api/curator/logout", { method: "POST" }),

  queue: () => apiSoft<CuratorPack[]>("/api/curator/queue"),
  all: (status?: string) =>
    apiSoft<CuratorPack[]>(`/api/curator/all${status ? `?status=${status}` : ""}`),
  analytics: () => apiSoft<CuratorAnalytics>("/api/curator/analytics"),

  approve: (id: string, notes?: string) =>
    apiCall<CuratorPack>(`/api/curator/packs/${id}/approve`, {
      method: "POST",
      body: { notes },
    }),
  reject: (id: string, notes: string) =>
    apiCall<CuratorPack>(`/api/curator/packs/${id}/reject`, {
      method: "POST",
      body: { notes },
    }),
  feature: (id: string) =>
    apiCall<CuratorPack>(`/api/curator/packs/${id}/feature`, { method: "POST" }),
  unfeature: (id: string) =>
    apiCall<CuratorPack>(`/api/curator/packs/${id}/unfeature`, { method: "POST" }),
  certify: (id: string) =>
    apiCall<CuratorPack>(`/api/curator/packs/${id}/certify`, { method: "POST" }),
  delete: (id: string) =>
    apiCall<void>(`/api/curator/packs/${id}`, { method: "DELETE" }),
  revert: (id: string) =>
    apiCall<CuratorPack>(`/api/curator/packs/${id}/revert`, { method: "POST" }),
  update: (id: string, body: Partial<Pick<CuratorPack, "name" | "description" | "industry_tags" | "icon_key">>) =>
    apiCall<CuratorPack>(`/api/curator/packs/${id}`, { method: "PUT", body }),
};
